/**
 * Kandles by LP Theme JavaScript
 *
 * All interactive functionality for the theme
 *
 * Contents:
 * 1. Utility Functions
 * 2. Mobile Menu
 * 3. Search Modal
 * 4. Cart Functionality
 * 5. Product Variants
 * 6. Product Image Gallery
 * 7. Add to Cart
 * 8. Modal Management
 * 9. Initialization
 */

(function() {
  'use strict';

  /* ============================================
     1. Utility Functions
     ============================================ */

  const utils = {
    // Format money using Shopify's format
    formatMoney(cents, format) {
      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }
      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || window.theme.moneyFormat || '${{amount}}';

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    },

    // Get cart via AJAX
    async getCart() {
      try {
        const response = await fetch('/cart.js');
        return await response.json();
      } catch (error) {
        console.error('Error fetching cart:', error);
        return null;
      }
    },

    // Update cart count in header
    updateCartCount(count) {
      const cartCountElements = document.querySelectorAll('[data-cart-count]');
      cartCountElements.forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    },

    // Debounce function
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  };

  /* ============================================
     2. Announcement Bar
     ============================================ */

  const announcementBar = {
    init() {
      const announcementBar = document.querySelector('[data-announcement-bar]');
      const closeButton = document.querySelector('[data-announcement-close]');

      if (!announcementBar || !closeButton) return;

      // Check if announcement was closed in this session only (not permanently)
      const isClosed = sessionStorage.getItem('announcement-bar-closed') === 'true';
      if (isClosed) {
        announcementBar.style.display = 'none';
      }

      // Close button functionality - only hide for current session
      closeButton.addEventListener('click', () => {
        announcementBar.style.display = 'none';
        sessionStorage.setItem('announcement-bar-closed', 'true');
      });
    }
  };

  /* ============================================
     3. Mobile Menu
     ============================================ */

  const mobileMenu = {
    init() {
      const toggle = document.querySelector('[data-mobile-menu-toggle]');
      const menu = document.querySelector('[data-mobile-menu]');

      if (!toggle || !menu) return;

      toggle.addEventListener('click', () => {
        const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', !isExpanded);
        menu.classList.toggle('active');
        menu.setAttribute('aria-hidden', isExpanded);

        if (!isExpanded) {
          menu.style.maxHeight = menu.scrollHeight + 'px';
        } else {
          menu.style.maxHeight = '0';
        }
      });
    }
  };

  /* ============================================
     3. Search Modal
     ============================================ */

  const searchModal = {
    modal: null,
    input: null,
    resultsContainer: null,

    init() {
      this.modal = document.querySelector('[data-search-modal]');
      this.input = document.querySelector('[data-search-input]');
      this.resultsContainer = document.querySelector('[data-search-results]');

      if (!this.modal) return;

      // Open modal
      document.querySelectorAll('[data-search-trigger]').forEach(trigger => {
        trigger.addEventListener('click', () => this.open());
      });

      // Close modal
      document.querySelectorAll('[data-search-close]').forEach(closeBtn => {
        closeBtn.addEventListener('click', () => this.close());
      });

      // Close on backdrop click
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close();
        }
      });

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('active')) {
          this.close();
        }
      });

      // Predictive search
      if (this.input) {
        this.input.addEventListener('input', utils.debounce((e) => {
          this.search(e.target.value);
        }, 300));
      }
    },

    open() {
      this.modal.classList.add('active');
      this.modal.setAttribute('aria-hidden', 'false');
      if (this.input) {
        setTimeout(() => this.input.focus(), 100);
      }
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.modal.classList.remove('active');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.input) {
        this.input.value = '';
      }
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = '';
      }
    },

    async search(query) {
      if (!query || query.length < 2) {
        this.resultsContainer.innerHTML = '';
        return;
      }

      try {
        const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`);
        const data = await response.json();

        this.displayResults(data.resources.results.products);
      } catch (error) {
        console.error('Search error:', error);
      }
    },

    displayResults(products) {
      if (!products || products.length === 0) {
        this.resultsContainer.innerHTML = '<p class="text-center text-text-light py-4">No results found</p>';
        return;
      }

      const html = products.map(product => `
        <a href="${product.url}" class="flex gap-4 p-3 hover:bg-gray-50 rounded transition-colors">
          <img src="${product.image}" alt="${product.title}" class="w-16 h-16 object-cover rounded">
          <div class="flex-1">
            <h3 class="font-semibold text-sm">${product.title}</h3>
            <p class="text-sm text-text-light">${utils.formatMoney(product.price, window.theme.moneyFormat)}</p>
          </div>
        </a>
      `).join('');

      this.resultsContainer.innerHTML = html;
    }
  };

  /* ============================================
     4. Cart Functionality
     ============================================ */

  const cart = {
    async init() {
      // Initialize cart count
      const cartData = await utils.getCart();
      if (cartData) {
        utils.updateCartCount(cartData.item_count);
      }
    }
  };

  /* ============================================
     5. Product Variants
     ============================================ */

  const productVariants = {
    init() {
      // Product card variant selectors (dropdowns - legacy)
      document.querySelectorAll('[data-product-variants]').forEach(select => {
        select.addEventListener('change', (e) => {
          this.updateProductCard(e.target);
        });
      });

      // Product card size buttons (new)
      document.querySelectorAll('.size-button').forEach(button => {
        button.addEventListener('click', (e) => {
          const btn = e.currentTarget;
          const productId = btn.getAttribute('data-product-id');
          const size = btn.getAttribute('data-size');

          // Update button states for this product
          const productButtons = document.querySelectorAll(`[data-product-id="${productId}"].size-button`);
          productButtons.forEach(b => {
            b.classList.remove('border-black', 'bg-black', 'text-white');
            b.classList.add('border-gray-300');
          });
          btn.classList.add('border-black', 'bg-black', 'text-white');
          btn.classList.remove('border-gray-300');

          // Find the variant with this size (ignoring color)
          const productJson = document.querySelector(`[data-product-json="${productId}"]`);
          if (productJson) {
            const productData = JSON.parse(productJson.textContent);
            // Find first available variant that matches the size
            const matchingVariant = productData.variants.find(v => {
              // Check if variant title contains the size
              const variantParts = v.title.split(' / ');
              return variantParts.some(part => part.trim() === size);
            });

            if (matchingVariant) {
              // Update the add to cart button with new variant ID
              const addToCartBtn = document.querySelector(`[data-add-to-cart="${productId}"]`);
              if (addToCartBtn) {
                addToCartBtn.setAttribute('data-variant-id', matchingVariant.id);
              }

              // Update price if different
              const priceContainer = document.querySelector(`[data-product-price="${productId}"]`);
              if (priceContainer && matchingVariant.price) {
                let priceHtml = `<span class="font-bold" style="font-family: 'Inter', sans-serif;">${utils.formatMoney(matchingVariant.price, window.theme.moneyFormat)}</span>`;

                if (matchingVariant.compare_at_price && parseInt(matchingVariant.compare_at_price) > parseInt(matchingVariant.price)) {
                  priceHtml = `
                    <div class="flex items-center gap-2" style="font-family: 'Inter', sans-serif;">
                      <span class="font-bold">${utils.formatMoney(matchingVariant.price, window.theme.moneyFormat)}</span>
                      <span class="text-text-light line-through text-sm opacity-70">${utils.formatMoney(matchingVariant.compare_at_price, window.theme.moneyFormat)}</span>
                    </div>
                  `;
                }

                priceContainer.innerHTML = priceHtml;
              }

              // Update image if variant has specific image
              if (matchingVariant.featured_image &&
                  matchingVariant.featured_image !== 'null' &&
                  matchingVariant.featured_image !== null &&
                  matchingVariant.featured_image !== '') {
                const productImage = document.querySelector(`[data-product-image="${productId}"]`);
                if (productImage && productImage.tagName === 'IMG') {
                  productImage.src = matchingVariant.featured_image;
                  productImage.srcset = '';  // Clear srcset to prevent conflicts
                }
              } else {
                // Fall back to product's default image if variant has no image
                const productImage = document.querySelector(`[data-product-image="${productId}"]`);
                if (productImage && productImage.tagName === 'IMG' && productData.featured_image) {
                  productImage.src = productData.featured_image;
                  productImage.srcset = '';
                }
              }
            }
          }
        });
      });

      // Product page variant selector (dropdown - legacy)
      const productSelect = document.querySelector('[data-product-select]');
      if (productSelect && productSelect.tagName === 'SELECT') {
        productSelect.addEventListener('change', (e) => {
          this.updateProductPage(e.target);
        });
      }

      // Product page variant selector (buttons) - legacy
      const variantButtons = document.querySelectorAll('.variant-button');
      variantButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          const btn = e.currentTarget;
          if (btn.disabled) return;

          // Update hidden input with selected variant ID
          const variantId = btn.getAttribute('data-variant-id');
          const hiddenInput = document.getElementById('product-variant-id');
          if (hiddenInput) {
            hiddenInput.value = variantId;
          }

          // Update button states
          variantButtons.forEach(b => {
            b.classList.remove('border-black', 'bg-black', 'text-white');
            b.classList.add('border-gray-300');
          });
          btn.classList.add('border-black', 'bg-black', 'text-white');
          btn.classList.remove('border-gray-300');

          // Update product page info
          this.updateProductPageFromButton(btn);
        });
      });

      // Product page variant selector (separate options - new)
      this.initSeparateVariantOptions();
    },

    updateProductCard(select) {
      const productId = select.getAttribute('data-product-variants');
      const selectedOption = select.options[select.selectedIndex];

      // Get data from selected option
      const newImage = selectedOption.getAttribute('data-image');
      const newPrice = selectedOption.getAttribute('data-price');
      const newComparePrice = selectedOption.getAttribute('data-compare-price');

      // Update image
      if (newImage && newImage !== 'null') {
        const productImage = document.querySelector(`[data-product-image="${productId}"]`);
        if (productImage) {
          productImage.src = newImage;
        }
      }

      // Update price
      if (newPrice) {
        const priceContainer = document.querySelector(`[data-product-price="${productId}"]`);
        if (priceContainer) {
          let priceHtml = `<span class="font-bold">${utils.formatMoney(newPrice, window.theme.moneyFormat)}</span>`;

          if (newComparePrice && parseInt(newComparePrice) > parseInt(newPrice)) {
            priceHtml = `
              <div class="flex items-center gap-2">
                <span class="font-bold">${utils.formatMoney(newPrice, window.theme.moneyFormat)}</span>
                <span class="text-text-light line-through text-sm opacity-70">${utils.formatMoney(newComparePrice, window.theme.moneyFormat)}</span>
              </div>
            `;
          }

          priceContainer.innerHTML = priceHtml;
        }
      }

      // Update add to cart button
      const addToCartBtn = document.querySelector(`[data-add-to-cart="${productId}"]`);
      if (addToCartBtn) {
        const variantId = selectedOption.value;
        addToCartBtn.setAttribute('data-variant-id', variantId);
      }
    },

    updateProductPage(select) {
      const selectedOption = select.options[select.selectedIndex];

      // Get data from selected option
      const newImage = selectedOption.getAttribute('data-image');
      const newPrice = selectedOption.getAttribute('data-price');
      const newComparePrice = selectedOption.getAttribute('data-compare-price');

      // Update main image
      if (newImage && newImage !== 'null') {
        const mainImage = document.getElementById('product-main-image');
        if (mainImage) {
          mainImage.src = newImage;
        }
      }

      // Update price
      if (newPrice) {
        const priceContainer = document.querySelector('[data-product-price]');
        if (priceContainer) {
          let priceHtml = `<span class="text-3xl font-bold">${utils.formatMoney(newPrice, window.theme.moneyFormat)}</span>`;

          if (newComparePrice && parseInt(newComparePrice) > parseInt(newPrice)) {
            priceHtml = `
              <div class="flex items-baseline gap-3">
                <span class="text-3xl font-bold">${utils.formatMoney(newPrice, window.theme.moneyFormat)}</span>
                <span class="text-xl text-text-light line-through opacity-70">${utils.formatMoney(newComparePrice, window.theme.moneyFormat)}</span>
              </div>
            `;
          }

          priceContainer.innerHTML = priceHtml;
        }
      }
    },

    updateProductPageFromButton(button) {
      // Get data from button attributes
      const newImage = button.getAttribute('data-image');
      const newPrice = button.getAttribute('data-price');
      const newComparePrice = button.getAttribute('data-compare-price');

      // Update main image
      if (newImage && newImage !== 'null') {
        const mainImage = document.getElementById('product-main-image');
        if (mainImage) {
          mainImage.src = newImage;
        }
      }

      // Update price
      if (newPrice) {
        const priceContainer = document.querySelector('[data-product-price]');
        if (priceContainer) {
          let priceHtml = `<span class="text-3xl font-bold">${utils.formatMoney(newPrice, window.theme.moneyFormat)}</span>`;

          if (newComparePrice && parseInt(newComparePrice) > parseInt(newPrice)) {
            priceHtml = `
              <div class="flex items-baseline gap-3">
                <span class="text-3xl font-bold">${utils.formatMoney(newPrice, window.theme.moneyFormat)}</span>
                <span class="text-xl text-text-light line-through opacity-70">${utils.formatMoney(newComparePrice, window.theme.moneyFormat)}</span>
              </div>
            `;
          }

          priceContainer.innerHTML = priceHtml;
        }
      }
    },

    initSeparateVariantOptions() {
      const optionButtons = document.querySelectorAll('.variant-option-button');
      if (optionButtons.length === 0) return;

      // Get product JSON data
      const productJson = document.getElementById('product-json');
      if (!productJson) return;

      const productData = JSON.parse(productJson.textContent);
      const selectedOptions = {};

      // Initialize first option values as selected
      optionButtons.forEach(btn => {
        const position = btn.getAttribute('data-option-position');
        const value = btn.getAttribute('data-option-value');
        const optionName = btn.getAttribute('data-option-name');

        if (!selectedOptions[position]) {
          selectedOptions[position] = value;
          btn.classList.add('border-black', 'bg-black', 'text-white');
          btn.classList.remove('border-gray-300');
        }
      });

      // Handle option button clicks
      optionButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          const btn = e.currentTarget;
          const position = btn.getAttribute('data-option-position');
          const value = btn.getAttribute('data-option-value');
          const optionName = btn.getAttribute('data-option-name');

          // Update selected options
          selectedOptions[position] = value;

          // Update button states for this option group
          const sameGroupButtons = document.querySelectorAll(`[data-option-position="${position}"]`);
          sameGroupButtons.forEach(b => {
            b.classList.remove('border-black', 'bg-black', 'text-white');
            b.classList.add('border-gray-300');
          });
          btn.classList.add('border-black', 'bg-black', 'text-white');
          btn.classList.remove('border-gray-300');

          // Find matching variant
          const matchingVariant = this.findVariantByOptions(productData.variants, selectedOptions);

          if (matchingVariant) {
            // Update hidden input
            const hiddenInput = document.getElementById('product-variant-id');
            if (hiddenInput) {
              hiddenInput.value = matchingVariant.id;
            }

            // Update price
            this.updatePriceDisplay(matchingVariant.price, matchingVariant.compare_at_price);

            // Update image if available
            const mainImage = document.getElementById('product-main-image');
            if (mainImage && mainImage.tagName === 'IMG') {
              if (matchingVariant.featured_image &&
                  matchingVariant.featured_image !== 'null' &&
                  matchingVariant.featured_image !== null &&
                  matchingVariant.featured_image !== '') {
                // Use variant-specific image
                mainImage.src = matchingVariant.featured_image;
                mainImage.srcset = '';  // Clear srcset to prevent conflicts
              } else if (productData.media && productData.media.length > 0) {
                // Fall back to first product image if variant has no image
                mainImage.src = productData.media[0].src;
                mainImage.srcset = '';
              }
            }
          }
        });
      });
    },

    findVariantByOptions(variants, selectedOptions) {
      return variants.find(variant => {
        // Build option string from selected options
        const optionKeys = Object.keys(selectedOptions).sort();
        const selectedTitle = optionKeys.map(key => selectedOptions[key]).join(' / ');
        return variant.title === selectedTitle;
      });
    },

    updatePriceDisplay(price, comparePrice) {
      const priceContainer = document.querySelector('[data-product-price]');
      if (!priceContainer) return;

      let priceHtml = `<span class="text-3xl font-bold">${utils.formatMoney(price, window.theme.moneyFormat)}</span>`;

      if (comparePrice && parseInt(comparePrice) > parseInt(price)) {
        priceHtml = `
          <div class="flex items-baseline gap-3">
            <span class="text-3xl font-bold">${utils.formatMoney(price, window.theme.moneyFormat)}</span>
            <span class="text-xl text-text-light line-through opacity-70">${utils.formatMoney(comparePrice, window.theme.moneyFormat)}</span>
          </div>
        `;
      }

      priceContainer.innerHTML = priceHtml;
    }
  };

  /* ============================================
     6. Product Image Gallery
     ============================================ */

  const productGallery = {
    init() {
      const thumbnails = document.querySelectorAll('[data-thumbnail]');
      const mainImage = document.getElementById('product-main-image');

      if (!mainImage || thumbnails.length === 0) return;

      thumbnails.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const newSrc = thumb.getAttribute('data-media-src');
          const newAlt = thumb.getAttribute('data-media-alt');

          if (newSrc) {
            mainImage.src = newSrc;
            if (newAlt) {
              mainImage.alt = newAlt;
            }

            // Update active state
            thumbnails.forEach(t => {
              t.classList.remove('border-black');
              t.classList.add('border-gray-300');
            });

            thumb.classList.remove('border-gray-300');
            thumb.classList.add('border-black');
          }
        });
      });
    }
  };

  /* ============================================
     7. Add to Cart
     ============================================ */

  const addToCart = {
    init() {
      // Product card add to cart buttons
      document.addEventListener('click', (e) => {
        const addToCartBtn = e.target.closest('[data-add-to-cart]');
        if (addToCartBtn) {
          e.preventDefault();
          this.handleAddToCart(addToCartBtn);
        }
      });

      // Product page form submission
      const productForm = document.querySelector('.product-form');
      if (productForm) {
        productForm.addEventListener('submit', (e) => {
          e.preventDefault();
          const formData = new FormData(e.target);
          this.addItemToCart(formData);
        });
      }
    },

    handleAddToCart(button) {
      const variantId = button.getAttribute('data-variant-id');
      const productId = button.getAttribute('data-add-to-cart');

      if (!variantId) return;

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);

      this.addItemToCart(formData, productId);
    },

    async addItemToCart(formData, productId = null) {
      try {
        // Show loading state
        const submitButton = document.querySelector('[data-add-to-cart-main], [data-add-to-cart]');
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.innerHTML = '<span class="spinner"></span>';
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });

        const item = await response.json();

        if (response.ok) {
          // Get updated cart
          const cartData = await utils.getCart();
          utils.updateCartCount(cartData.item_count);

          // Show cart modal if enabled
          if (window.theme.settings.cartType === 'page' || !window.theme.settings.cartType) {
            cartModal.show(item, cartData);
          }
        } else {
          alert('Sorry, this product could not be added to your cart.');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        alert('An error occurred. Please try again.');
      } finally {
        // Reset button
        const submitButton = document.querySelector('[data-add-to-cart-main], [data-add-to-cart]');
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = window.theme.strings.addToCart || 'Add To Bag';
        }
      }
    }
  };

  /* ============================================
     8. Cart Modal
     ============================================ */

  const cartModal = {
    modal: null,

    init() {
      this.modal = document.querySelector('[data-cart-modal]');

      if (!this.modal) return;

      // Close modal
      document.querySelectorAll('[data-cart-modal-close]').forEach(closeBtn => {
        closeBtn.addEventListener('click', () => this.close());
      });

      // Close on backdrop click
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal) {
          this.close();
        }
      });

      // Close on Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal.classList.contains('active')) {
          this.close();
        }
      });
    },

    show(item, cartData) {
      if (!this.modal) return;

      // Update modal content
      const modalImage = this.modal.querySelector('[data-cart-modal-image]');
      const modalTitle = this.modal.querySelector('[data-cart-modal-product-title]');
      const modalVariant = this.modal.querySelector('[data-cart-modal-variant]');
      const modalPrice = this.modal.querySelector('[data-cart-modal-price]');
      const modalMessage = this.modal.querySelector('[data-cart-modal-message]');
      const modalTotal = this.modal.querySelector('[data-cart-modal-total]');

      if (modalImage) modalImage.src = item.image;
      if (modalImage) modalImage.alt = item.title;
      if (modalTitle) modalTitle.textContent = item.product_title;
      if (modalVariant) modalVariant.textContent = item.variant_title !== 'Default Title' ? item.variant_title : '';
      if (modalPrice) modalPrice.textContent = utils.formatMoney(item.price, window.theme.moneyFormat);

      if (modalMessage) {
        const itemText = cartData.item_count === 1 ? 'item' : 'items';
        modalMessage.textContent = `You have ${cartData.item_count} ${itemText} in your cart`;
      }

      if (modalTotal) modalTotal.textContent = utils.formatMoney(cartData.total_price, window.theme.moneyFormat);

      // Show modal
      this.modal.classList.add('active');
      this.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    },

    close() {
      if (!this.modal) return;

      this.modal.classList.remove('active');
      this.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
  };

  /* ============================================
     9. Initialization
     ============================================ */

  function init() {
    // Initialize all modules
    announcementBar.init();
    mobileMenu.init();
    searchModal.init();
    cart.init();
    productVariants.init();
    productGallery.init();
    addToCart.init();
    cartModal.init();

    console.log('Kandles by LP theme initialized');
  }

  // Run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
